/* =============================================================
   studentCategory.js — Student category logic
   -------------------------------------------------------------
   A student's "category" is the combination of their exchange
   type + EU status. It determines which documents are required.

   Categories:
     erasmus              — Erasmus (implicitly EU)
     bilateral_eu         — Bilateral agreement, EU nationality
     bilateral_extra_eu   — Bilateral agreement, non-EU nationality
     double_degree_eu     — Double Degree, EU nationality
     double_degree_extra_eu — Double Degree, non-EU nationality
   ============================================================= */

export const STUDENT_CATEGORIES = [
  { value: 'erasmus',                label: 'Erasmus'                  },
  { value: 'bilateral_eu',           label: 'Bilateral · EU'           },
  { value: 'bilateral_extra_eu',     label: 'Bilateral · Extra-EU'     },
  { value: 'double_degree_eu',       label: 'Double Degree · EU'       },
  { value: 'double_degree_extra_eu', label: 'Double Degree · Extra-EU' },
];

/**
 * Returns the category string for a student given their exchange type
 * and EU status. Returns null if either field is missing.
 */
export function getStudentCategory(exchangeType, euStatus) {
  if (!exchangeType) return null;
  if (exchangeType === 'erasmus') return 'erasmus';
  const suffix = euStatus === 'eu' ? '_eu' : '_extra_eu';
  return `${exchangeType}${suffix}`;
}

/**
 * Returns true if a document type is required for a given student category.
 * An empty required_for array means "required for all" (backward compatible).
 */
export function isDocumentRequired(documentType, studentCategory) {
  const rf = documentType?.required_for;
  if (!rf || rf.length === 0) return true;   // required for all
  if (!studentCategory) return true;          // unknown category → show all
  return rf.includes(studentCategory);
}


/* ─── DEGREE LEVELS ──────────────────────────────────────────── */
export const DEGREE_LEVELS = [
  { value: 'bachelor', label: 'Bachelor' },
  { value: 'master',   label: 'Master'   },
  { value: 'mba',      label: 'MBA'      },
  { value: 'other',    label: 'Other'    },
];


/**
 * Resolves which process template applies to a student.
 *
 * templates       — array of university_process_templates for this type
 * student         — { degree_level, exchange_type, eu_status, process_template_id }
 *
 * Priority:
 *   1. Manual override (process_template_id set on student)
 *   2. Template whose match_degree includes the student's degree_level
 *      AND (match_categories is empty OR includes the student's category)
 *   3. Default template (is_default = true)
 *   4. First template (last resort)
 *
 * Returns the matching template object, or null if no templates exist.
 */
/**
 * Resolves which process template applies to a student.
 *
 * templates  — array of university_process_templates for this type
 * student    — { degree_level, exchange_type, eu_status,
 *               process_template_id, semester_id }
 * semesters  — array of semesters rows (optional)
 *
 * Priority:
 *   1. Manual override (process_template_id set directly on student)
 *   2. Semester's template (student.semester_id → semester.process_template_id)
 *   3. Template whose match_degree includes the student's degree_level
 *      AND (match_categories is empty OR includes the student's category)
 *   4. Default template (is_default = true)
 *   5. First template (last resort)
 */
export function resolveProcessTemplate(templates, student, semesters = []) {
  if (!templates || templates.length === 0) return null;

  // Normalise: accept both snake_case DB rows and camelCase UI-mapped objects
  const processTemplateId = student.process_template_id ?? student.processTemplateId ?? null;
  const semesterId        = student.semester_id         ?? student.semesterId        ?? null;
  const exchangeType      = student.exchange_type       ?? student.exchangeType      ?? null;
  const euStatus          = student.eu_status           ?? student.euStatus          ?? null;
  const degreeLevel       = student.degree_level        ?? student.degreeLevel       ?? null;

  // 1. Manual override
  if (processTemplateId) {
    const manual = templates.find(t => t.id === processTemplateId);
    if (manual) return manual;
  }

  // 2. Filter to templates belonging to the student's semester.
  //    If none found (e.g. legacy student without semester_id), fall back to all.
  const semesterTemplates = semesterId
    ? templates.filter(t => t.semester_id === semesterId)
    : templates;
  const pool = semesterTemplates.length > 0 ? semesterTemplates : templates;

  const category = getStudentCategory(exchangeType, euStatus);

  // Helper: a template is eligible for this student if its match arrays are empty
  // (= applies to everyone) OR the student's value is included.
  // If the student has no degree/category set, unrestricted templates still match.
  const eligible = (t) => {
    const degreeOk   = !t.match_degree?.length    || !degreeLevel || t.match_degree.includes(degreeLevel);
    const categoryOk = !t.match_categories?.length || !category   || t.match_categories.includes(category);
    return degreeOk && categoryOk;
  };

  // 3. Specific match: degree + category within semester pool
  if (degreeLevel) {
    const specific = pool.find(t => {
      const degreeMatch   = t.match_degree?.includes(degreeLevel);
      const categoryMatch = !t.match_categories?.length || (category && t.match_categories.includes(category));
      return degreeMatch && categoryMatch;
    });
    if (specific) return specific;

    // 3b. Degree match only (any category)
    const degreeOnly = pool.find(t => t.match_degree?.includes(degreeLevel));
    if (degreeOnly) return degreeOnly;
  }

  // 4. Default template — only if the student is eligible for it.
  //    A template marked "Master + Default" is NOT a fallback for Bachelor students.
  const def = pool.find(t => t.is_default && eligible(t));
  if (def) return def;

  // 5. Last resort: first eligible template in pool.
  //    If no template in the pool accepts this student, return null
  //    so the profile shows "No process configured" instead of the wrong process.
  return pool.find(t => eligible(t)) ?? null;
}

/**
 * Builds a flat item list from the multi-doc/task step structure.
 *
 * steps           — array of process_steps rows, each with:
 *                     process_step_documents: [{ id, item_type, task_label,
 *                       document_types: { name, icon, required_for, ... } }]
 * uploads         — array from useStudentDocuments (docName, status, …)
 * studentCategory — result of getStudentCategory()
 * taskCompletions — Set<process_step_document_id> of completed tasks
 *
 * Returns an array of:
 *   { junctionId, name, icon, stepIndex, item_type,
 *     effectiveStatus, upload, uploadable,
 *     deadline_date, deadlineDays,
 *     template_storage_path, template_file_name }
 *
 * effectiveStatus values:
 *   documents → 'missing' | 'pending' | 'approved' | 'rejected'
 *   tasks     → 'task_done' | 'task_pending'
 */
export function buildDocList(steps, uploads, studentCategory, taskCompletions = new Set()) {
  const today = new Date(); today.setHours(0, 0, 0, 0);

  return steps.flatMap((step, stepIndex) => {
    const psdList = step.process_step_documents ?? [];

    return psdList
      .filter(psd => {
        if (psd.item_type === 'task') return true; // tasks always shown
        return psd.document_types != null;          // all docs in the process are shown
      })
      .map(psd => {
        const isTask = psd.item_type === 'task';
        const dt     = psd.document_types ?? null;

        if (isTask) {
          const deadlineDays = psd.deadline_date
            ? Math.round((new Date(psd.deadline_date + 'T00:00:00') - today) / 86_400_000)
            : null;
          return {
            junctionId:            psd.id,
            name:                  psd.document_types?.name ?? 'Task',
            icon:                  'check_box',
            stepIndex,
            item_type:             'task',
            effectiveStatus:       taskCompletions.has(psd.id) ? 'task_done' : 'task_pending',
            upload:                null,
            uploadable:            false,
            deadline_date:         psd.deadline_date ?? null,
            deadlineDays,
            template_storage_path: null,
            template_file_name:    null,
          };
        }

        // Document item
        const deadlineDays = psd.deadline_date
          ? Math.round((new Date(psd.deadline_date + 'T00:00:00') - today) / 86_400_000)
          : null;

        const upload = uploads.find(u => u.docName === dt.name) ?? null;
        return {
          junctionId:            psd.id,
          name:                  dt.name,
          icon:                  dt.icon ?? 'draft',
          stepIndex,
          item_type:             'document',
          effectiveStatus:       upload?.status ?? 'missing',
          upload,
          uploadable:            (upload?.status ?? 'missing') !== 'not_required',
          deadline_date:         psd.deadline_date ?? null,
          deadlineDays,
          template_storage_path: dt.template_storage_path ?? null,
          template_file_name:    dt.template_file_name    ?? null,
        };
      });
  });
}
